import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./dialog";
import { Button } from "./button";
import { X } from 'lucide-react';

const operators = [
  { label: "jest równe", value: "==" },
  { label: "jest różne", value: "!=" },
];

function escapeValue(val: string) {
  // Jeśli liczba, nie dodawaj apostrofów
  return isNaN(Number(val)) ? `'${val}'` : val;
}

export type ConditionBlock = {
  type: "if" | "elseif" | "else";
  variable?: string;
  operator?: string;
  value?: string;
  output: string;
};

interface FormulaBuilderModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  variables: string[];
}

export function FormulaBuilderModal({ open, onOpenChange, variables }: FormulaBuilderModalProps) {
  const [blocks, setBlocks] = useState<ConditionBlock[]>([
    { type: "if", variable: "", operator: "==", value: "", output: "" },
  ]);
  const [formula, setFormula] = useState("");
  const [copied, setCopied] = useState(false);
  const [variableSuggestions, setVariableSuggestions] = useState<string[]>([]);
  const [variableInput, setVariableInput] = useState<string>("");
  const [focusedBlockIdx, setFocusedBlockIdx] = useState<number | null>(null);

  // Dodawanie bloków
  const addElseIf = () => setBlocks([...blocks, { type: "elseif", variable: "", operator: "==", value: "", output: "" }]);
  const addElse = () => setBlocks([...blocks, { type: "else", output: "" }]);

  // Aktualizacja bloku
  const updateBlock = (idx: number, patch: Partial<ConditionBlock>) => {
    setBlocks(blocks.map((b, i) => (i === idx ? { ...b, ...patch } : b)));
  };

  // Generowanie formuły
  const generateFormula = () => {
    let f = "";
    blocks.forEach((b, i) => {
      if (b.type === "if") f += `<<#if ${b.variable} ${b.operator} ${escapeValue(b.value || "") }>>${b.output}`;
      if (b.type === "elseif") f += `<#elseif ${b.variable} ${b.operator} ${escapeValue(b.value || "") }>>${b.output}`;
      if (b.type === "else") f += `<#else>>${b.output}`;
    });
    f += "<</if>>";
    setFormula(f);
  };

  // Kopiowanie do schowka
  const handleCopy = () => {
    navigator.clipboard.writeText(formula);
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
  };

  // Czyszczenie formularza
  const handleClear = () => {
    setBlocks([{ type: "if", variable: "", operator: "==", value: "", output: "" }]);
    setFormula("");
  };

  // Automatycznie generuj formułę na zmianę bloków
  React.useEffect(() => { generateFormula(); }, [blocks]);

  // Resetuj po zamknięciu
  React.useEffect(() => { if (!open) setBlocks([{ type: "if", variable: "", operator: "==", value: "", output: "" }]); }, [open]);

  // Podpowiedzi zmiennych
  const handleVariableInput = (idx: number, value: string) => {
    updateBlock(idx, { variable: value });
    setVariableInput(value);
    setVariableSuggestions(
      value
        ? variables.filter(v => v.toLowerCase().includes(value.toLowerCase()))
        : []
    );
    setFocusedBlockIdx(idx);
  };

  const handleSuggestionClick = (idx: number, suggestion: string) => {
    updateBlock(idx, { variable: suggestion });
    setVariableInput("");
    setVariableSuggestions([]);
    setFocusedBlockIdx(null);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogHeader>
        <DialogTitle>Kreator Formuł Logicznych</DialogTitle>
      </DialogHeader>
      <DialogContent>
        <div style={{marginBottom: 18, color: '#2563eb', fontWeight: 600, fontSize: 16}}>
          Zbuduj warunek IF/ELSE do Worda – możesz wpisać dowolną nazwę zmiennej lub wybrać z podpowiedzi.
        </div>

        <div style={{marginTop: 18, color: '#2563eb', fontWeight: 600, fontSize: 16}}>
          <span>Każda nazwa zmiennej, którą wpiszesz po IF w kreatorze (np. <code>status</code> w <code>{'<<#if status == \'Aktywna\'>>'}</code>), będzie widoczna do uzupełnienia w formularzu oraz w pliku Excel. To, co wpisze użytkownik, zadecyduje o tym, jaki tekst pojawi się w dokumencie.</span>
        </div>
        <div style={{marginBottom: 24, background: '#f6f8fa', borderRadius: 10, padding: 16, color: '#222', fontSize: 15, lineHeight: 1.7}}>
          <b>Czym są IF, ELSE, ELSE IF?</b><br/>
          <ul style={{margin: '8px 0 0 18px', padding: 0}}>
            <li><b>IF</b> – "jeśli". Pozwala wstawić tekst tylko wtedy, gdy wybrana zmienna ma określoną wartość (np. jeśli status == "Aktywna").</li>
            <li><b>ELSE IF</b> – "w przeciwnym razie, jeśli". Pozwala dodać kolejny warunek, jeśli pierwszy nie był spełniony.</li>
            <li><b>ELSE</b> – "w przeciwnym razie". Pozwala wstawić tekst, jeśli żaden z wcześniejszych warunków nie był spełniony.</li>
          </ul>
          <div style={{marginTop: 8, color: '#64748b', fontSize: 14}}>
            Przykład: <br/>
            <code>{`<<#if status == 'Aktywna'>>Umowa jest aktywna.<</if>>`}</code>
          </div>
        </div>
        {blocks.map((block, idx) => (
          <div key={idx} style={{ background: "#f6f8fa", borderRadius: 10, padding: 18, boxShadow: "0 2px 8px #e0e7ff33", position: 'relative', marginBottom: 18 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 16, width: '100%' }}>
              <div style={{ width: 80, minWidth: 80, fontWeight: 700, fontSize: 16, display: 'flex', alignItems: 'center' }}>
                {block.type === "if" ? "IF" : block.type === "elseif" ? "ELSE IF" : "ELSE"}
                {block.type !== 'if' && (
                  <button
                    onClick={() => setBlocks(blocks.filter((_, i) => i !== idx))}
                    style={{
                      marginLeft: 8,
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: 4,
                      borderRadius: 6,
                      color: '#888',
                      fontSize: 18,
                      lineHeight: 1,
                    }}
                    title="Usuń blok"
                    aria-label="Usuń blok"
                  >
                    <X size={20} />
                  </button>
                )}
              </div>
              {block.type !== "else" && (
                <>
                  <div style={{ flex: '1 1 40%', minWidth: 180, maxWidth: '40%' }}>
                    <input
                      type="text"
                      value={block.variable || ""}
                      onChange={e => handleVariableInput(idx, e.target.value)}
                      placeholder="Nazwa zmiennej (np. status)"
                      style={{ width: '100%', minWidth: 0, padding: '14px 18px', borderRadius: 10, border: '2px solid #2563eb', fontSize: 19, fontWeight: 600, background: '#fff', marginBottom: 2 }}
                      autoFocus={focusedBlockIdx === idx}
                      onFocus={() => setFocusedBlockIdx(idx)}
                      autoComplete="off"
                    />
                    {focusedBlockIdx === idx && variableSuggestions.length > 0 && (
                      <div style={{ position: 'absolute', top: 54, left: 0, background: '#fff', border: '2px solid #2563eb', borderRadius: 10, zIndex: 10, minWidth: 400, width: '100%', boxShadow: '0 2px 8px #e0e7ff33' }}>
                        {variableSuggestions.map(s => (
                          <div key={s} style={{ padding: 12, cursor: 'pointer', fontSize: 17 }} onMouseDown={() => handleSuggestionClick(idx, s)}>{s}</div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div style={{ flex: '1 1 10%', minWidth: 90, maxWidth: '10%' }}>
                    <select value={block.operator} onChange={e => updateBlock(idx, { operator: e.target.value })} style={{ width: '100%', minWidth: 0, padding: '14px 8px', borderRadius: 10, fontSize: 18, fontWeight: 600, background: '#fff', border: '2px solid #2563eb' }}>
                      {operators.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </div>
                  <div style={{ flex: '1 1 40%', minWidth: 180, maxWidth: '40%' }}>
                    <input type="text" value={block.value || ""} onChange={e => updateBlock(idx, { value: e.target.value })} placeholder="Wartość" style={{ width: '100%', minWidth: 0, padding: '14px 18px', borderRadius: 10, border: '2px solid #2563eb', fontSize: 18, fontWeight: 600, background: '#fff' }} />
                  </div>
                </>
              )}
            </div>
            <div style={{ marginTop: 18, display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ fontWeight: 400, color: "#888", minWidth: 120 }}>Wstaw do dokumentu:</span>
              <input type="text" value={block.output} onChange={e => updateBlock(idx, { output: e.target.value })} placeholder="Tekst do wstawienia" style={{ minWidth: 400, width: '100%', padding: '14px 18px', borderRadius: 10, border: '2px solid #2563eb', fontSize: 18, fontWeight: 600, background: '#fff' }} />
            </div>
          </div>
        ))}
        <div style={{ display: "flex", gap: 16, marginBottom: 24 }}>
          <Button onClick={addElseIf} style={{ fontSize: 15, padding: "8px 20px", borderRadius: 8, background: '#e0e7ff', color: '#2563eb', fontWeight: 700 }}>+ Dodaj warunek ELSE IF</Button>
          {!blocks.some(b => b.type === "else") && (
            <Button onClick={addElse} style={{ fontSize: 15, padding: "8px 20px", borderRadius: 8, background: '#e0e7ff', color: '#2563eb', fontWeight: 700 }}>+ Dodaj blok ELSE</Button>
          )}
        </div>
        <div style={{ background: "#f1f5f9", borderRadius: 10, padding: 18, marginBottom: 10, fontFamily: "monospace", fontSize: 17, color: "#222", boxShadow: "0 2px 8px #e0e7ff33" }}>
          <div style={{ fontWeight: 700, color: '#2563eb', marginBottom: 6 }}>Podgląd wygenerowanej formuły:</div>
          {formula}
        </div>
        <div style={{ color: '#64748b', fontSize: 15, marginBottom: 18 }}>
          Skopiuj powyższą formułę i wklej ją w odpowiednie miejsce w szablonie Word. Możesz używać dowolnych nazw zmiennych – ważne, by były zgodne z tymi w szablonie.
        </div>
        <div style={{display: 'flex', gap: 12, alignItems: 'center'}}>
          <Button onClick={handleCopy} style={{ fontSize: 16, padding: "12px 32px", background: copied ? "#22c55e" : "#2563eb", color: "#fff", borderRadius: 10, fontWeight: 700, boxShadow: '0 2px 8px #e0e7ff33' }}>
            {copied ? "Skopiowano!" : "Kopiuj do schowka"}
          </Button>
          <Button onClick={handleClear} style={{ fontSize: 16, padding: "12px 24px", color: '#2563eb', background: '#fff', border: '2px solid #2563eb', fontWeight: 700, borderRadius: 10 }}>
            Wyczyść
          </Button>
        </div>
      </DialogContent>
      <DialogFooter>
        <div style={{ width: '100%', display: 'flex', justifyContent: 'flex-end' }}>
          <Button onClick={() => onOpenChange(false)} style={{ fontSize: 15, padding: "10px 28px", borderRadius: 8, background: '#fff', color: '#2563eb', border: '2px solid #2563eb', fontWeight: 700 }}>
            Zamknij
          </Button>
        </div>
      </DialogFooter>
    </Dialog>
  );
} 